import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Search, X, ScanLine, PenLine } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { ImportSelectionSheet } from '../components/savedSelections/ImportSelectionSheet';
import { SavedSelectionsView, started } from '../components/savedSelections/SavedSelectionsView';
import { SaveNotifyAsk } from '../components/savedSelections/SaveNotifyAsk';
import { DiaryView } from '../components/savedSelections/DiaryView';
import { SelectionEditor } from '../components/savedSelections/SelectionEditor';
import { useScreenProtection } from '../hooks/useScreenProtection';
import { useVipGuardActive } from '../lib/vipGuard';

const TABS = [['live', 'In corso'], ['scheduled', 'Programmate'], ['archive', 'Concluse']];

const Tabs = ({ tab, setTab }) => (
  <div className="flex border-b border-white/10 mb-3" data-testid="diario-tabs">
    {TABS.map(([k, l]) => (
      <button key={k} type="button" onClick={() => setTab(k)} data-testid={`diario-tab-${k}`}
        className={`flex-1 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === k ? 'border-[#F5A623] text-[#F5A623]' : 'border-transparent text-gray-400'}`}>{l}</button>
    ))}
  </div>
);

const SearchBox = ({ value, onChange }) => (
  <div className="flex items-center gap-2 bg-[#1A1A1A] border border-white/10 rounded-xl px-3 mb-4">
    <Search className="w-4 h-4 text-gray-500" />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Cerca squadra, nome o codice" className="flex-1 bg-transparent py-2.5 text-white text-sm outline-none" data-testid="diario-search" />
    {value && <button type="button" onClick={() => onChange('')} className="text-gray-500" data-testid="diario-search-clear"><X className="w-4 h-4" /></button>}
  </div>
);

const AddMenu = ({ onImport, onCreate, onClose }) => (
  <div className="fixed inset-0 z-[60] bg-black/60" onClick={onClose} data-testid="diario-add-menu">
    <div className="absolute right-4 bottom-[calc(10.5rem+env(safe-area-inset-bottom,0px))] w-60 rounded-2xl bg-[#1F1F1F] border border-white/10 overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={onImport} className="w-full px-4 py-3.5 flex items-center gap-3 text-white font-bold text-sm text-left" data-testid="diario-add-import">
        <ScanLine className="w-5 h-5 text-[#F5A623]" /> Importa da screenshot
      </button>
      <button type="button" onClick={onCreate} className="w-full px-4 py-3.5 flex items-center gap-3 text-white font-bold text-sm text-left border-t border-white/10" data-testid="diario-add-create">
        <PenLine className="w-5 h-5 text-[#F5A623]" /> Crea a mano
      </button>
    </div>
  </div>
);

// Bottom tab "Diario" (VIP/GOLD): In corso / Programmate / Concluse, search, "+" to import or create
export default function DiarioPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const premium = !!(user && (user.is_vip || user.is_gold || user.is_admin));
  const [tab, setTab] = useState('live');
  const [query, setQuery] = useState('');
  const [menu, setMenu] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editor, setEditor] = useState(null); // { initial, source } new/imported, or { existing }
  // "Selezione Vincenzo" copies get the same screen protection as the VIP area (admin excluded, like there)
  const vipOnScreen = useVipGuardActive() || editor?.source === 'vip' || !!editor?.existing?.vip_copy;
  useScreenProtection(!vipOnScreen || !!user?.is_admin);
  const [reload, setReload] = useState(0);
  const [askId, setAskId] = useState(null);
  const [nudgeId, setNudgeId] = useState(null);

  useEffect(() => {
    if (!loading && user && !premium) navigate('/vip?da=diario', { replace: true });
  }, [loading, user, premium, navigate]);

  useEffect(() => {
    const draft = location.state?.aiDraft;
    const vip = location.state?.vipDraft;
    if ((!draft && !vip) || !premium) return;
    setEditor(vip ? { initial: { ...vip, openSave: true }, source: 'vip' } : { initial: draft, source: 'ia' });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, premium, navigate]);

  if (!premium) return null;
  const refresh = () => setReload((n) => n + 1);
  // after Salva: straight to the tab where the selection now lives
  const afterSave = (sel, isNew) => {
    refresh();
    if (!sel) return;
    setTab(sel.status === 'archived' ? 'archive' : started(sel) ? 'live' : 'scheduled');
    if (isNew && !sel.notify) setAskId(sel.selection_id); // Vincenzo copy already followed in Area VIP: no question
    if (isNew && !sel.vip_copy) setNudgeId(sel.selection_id); // "Condividila con un amico" box, not on Vincenzo copies
  };

  return (
    <div className="min-h-screen bg-[#121212] px-4 pt-6 pb-40 max-w-md mx-auto" data-testid="diario-page">
      <h1 className="text-white font-black text-3xl mb-3" style={{ fontFamily: 'Barlow Condensed' }}>Diario</h1>
      <Tabs tab={tab} setTab={setTab} />
      <SearchBox value={query} onChange={setQuery} />
      {tab === 'archive'
        ? <DiaryView query={query} reloadKey={reload} onEdit={(s) => setEditor({ existing: s })} />
        : <SavedSelectionsView key={tab} tab={tab} query={query} reloadKey={reload} onEdit={(s) => setEditor({ existing: s })} nudgeId={askId ? null : nudgeId} />}
      <button type="button" onClick={() => setMenu(true)} aria-label="Aggiungi selezione"
        className="fixed right-4 bottom-[calc(6rem+env(safe-area-inset-bottom,0px))] z-40 w-16 h-16 rounded-full bg-[#F5A623] text-[#1A1A1A] flex items-center justify-center shadow-[0_8px_28px_rgba(245,166,35,0.45)] active:scale-95 transition-transform" data-testid="diario-add-btn">
        <Plus className="w-8 h-8" strokeWidth={3} />
      </button>
      {menu && <AddMenu onClose={() => setMenu(false)} onImport={() => { setMenu(false); setImporting(true); }} onCreate={() => { setMenu(false); setEditor({ initial: {}, source: 'manual' }); }} />}
      {importing && (
        <ImportSelectionSheet onClose={() => setImporting(false)}
          onRead={(data) => { setImporting(false); setEditor({ initial: data, source: 'import' }); }}
          onCreate={() => { setImporting(false); setEditor({ initial: {}, source: 'manual' }); }} />
      )}
      {editor && <SelectionEditor initial={editor.initial} existing={editor.existing} source={editor.source} onClose={() => setEditor(null)} onSaved={afterSave} />}
      {askId && <div className="relative z-[9999]"><SaveNotifyAsk selectionId={askId} onDone={() => { setAskId(null); refresh(); }} /></div>}
      <BottomNav />
    </div>
  );
}
